import { useQuery } from "@tanstack/vue-query";
import { computed, ComputedRef, Ref, unref } from "vue";
import Meeting from "@/services/meeting";

export interface UseRepDayMeetingsParams {
  userId?: string;
  dateISO?: string;
  organizationId?: string;
  timezone?: string;
}

export interface RepDayMeetingsResult {
  busySlots: ComputedRef<string[]>;
  isLoading: Ref<boolean>;
  meetings: ComputedRef<any[]>;
}

/**
 * Fetch the rep's booked meetings for a specific day and return busy time slots
 * Expands each meeting across 15-min intervals based on duration
 * Also fetches external calendar busy slots (Google / Outlook)
 */
export function useRepDayMeetings(params: UseRepDayMeetingsParams): RepDayMeetingsResult {
  const { userId, dateISO, organizationId, timezone } = params;

  // Compute day boundaries in UTC from local midnight
  const dayBoundaries = computed(() => {
    if (!dateISO) return null;

    const d = new Date(dateISO);
    const year = d.getFullYear();
    const month = d.getMonth();
    const day = d.getDate();

    const startOfDay = new Date(year, month, day, 0, 0, 0, 0).toISOString();
    const endOfDay = new Date(year, month, day, 23, 59, 59, 999).toISOString();

    return { startOfDay, endOfDay };
  });

  // TanStack Query for internal SalesBuckets meetings on this day
  const { data: meetingsData, isLoading: internalLoading } = useQuery({
    queryKey: ["rep-day-meetings", userId, dateISO, organizationId],
    queryFn: async () => {
      if (!userId || !dateISO || !organizationId) {
        return [];
      }

      const boundaries = dayBoundaries.value;
      if (!boundaries) return [];

      const response = await Meeting.getMeetings({
        userId,
        organizationId,
        bookedDate: [boundaries.startOfDay, boundaries.endOfDay],
        status: "pending",
        limit: 100,
      });

      if (response.data?.status) {
        return response.data.data || [];
      }

      return [];
    },
    enabled: computed(
      () => !!unref(userId) && !!unref(dateISO) && !!unref(organizationId) && !!unref(timezone)
    ),
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
  });

  // TanStack Query for external calendar busy slots (Google / Outlook)
  const { data: externalData, isLoading: externalLoading } = useQuery({
    queryKey: ["rep-external-busy", unref(dateISO), unref(timezone)],
    queryFn: async () => {
      const dateValue = unref(dateISO) as string;
      const tzValue = unref(timezone) as string;
      if (!dateValue || !tzValue) return { busySlots: [], errors: {} };
      try {
        const resp = await Meeting.getExternalBusySlots({
          date: dateValue.split("T")[0],
          timezone: tzValue,
        });
        return resp.data?.status ? resp.data.data : { busySlots: [], errors: {} };
      } catch {
        return { busySlots: [], errors: {} }; // never block the time selector
      }
    },
    enabled: computed(() => !!unref(dateISO) && !!unref(timezone)),
    staleTime: 5 * 60 * 1000, // 5 min — calendar events rarely change mid-session
    gcTime: 10 * 60 * 1000,
  });

  // Meetings as computed
  const meetings = computed(() => meetingsData.value || []);

  // Expand internal meetings + merge with external calendar busy slots
  const busySlots = computed(() => {
    if (!timezone) return [];

    const slots = new Set<string>();

    // Internal meetings — expand each across 15-min intervals
    meetings.value.forEach((meeting: any) => {
      const start = new Date(meeting.meetingDateTime);
      const duration = meeting.duration || 30; // default 30 min
      const slotCount = Math.ceil(duration / 15);

      for (let i = 0; i < slotCount; i++) {
        const slotTime = new Date(start.getTime() + i * 15 * 60 * 1000);

        // Convert UTC time to selected timezone using Intl
        const formatter = new Intl.DateTimeFormat("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
          timeZone: timezone,
        });

        const formatted = formatter.format(slotTime);
        slots.add(formatted);
      }
    });

    // External calendar slots — already HH:MM strings from backend
    (externalData.value?.busySlots ?? []).forEach((slot: string) => slots.add(slot));

    return Array.from(slots).sort();
  });

  return {
    busySlots,
    isLoading: computed(() => internalLoading.value || externalLoading.value),
    meetings,
  };
}
